import { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useCurrentUser } from '@mews-commander/core';
import { MewsBIIntegrationUser } from '@mews-commander/data-access';
import { SelectField, SelectFieldProps } from '@mews-commander/form';
import { createOption } from '@mews-commander/ui';
import { useFieldDependency } from '@mews/framework';
import { useAllAvailableUsersQuery } from '../hooks';
import { FormValues } from '../schema';

const VIRTUALIZED_OPTIONS_LIMIT = 50;

const renderOptions = (users: Array<MewsBIIntegrationUser>, currentUserId: string, disableCurrentUser = true) =>
    users.map((user) =>
        createOption({
            value: user.id,
            label: user.name,
            disabled: disableCurrentUser && user.id === currentUserId,
        })
    );

export const UsersField = (props: Pick<SelectFieldProps, 'label' | 'name'> & { disableCurrentUser?: boolean }) => {
    const { disableCurrentUser = true, ...selectProps } = props;
    const { data, isLoading } = useAllAvailableUsersQuery();
    const currentUser = useCurrentUser();

    const { dependencyValue: selectedEnterpriseIds } = useFieldDependency<FormValues, 'enterpriseIds'>({
        name: 'userIds',
        dependency: 'enterpriseIds',
        setInitialValueDisabled: true,
    });
    const enterpriseSet = useMemo(() => new Set(selectedEnterpriseIds), [selectedEnterpriseIds]);

    const users = useMemo(() => {
        const allUsers = data?.users || [];
        const filteredUsers = allUsers.filter(
            (user) => new Set(user.enterpriseIds).intersection(enterpriseSet).size > 0
        );
        return filteredUsers.toSorted((a, b) => a.name.localeCompare(b.name));
    }, [data, enterpriseSet]);

    const { setValue, getValues } = useFormContext<FormValues>();

    // Clear values from select state that are no longer in options
    useEffect(() => {
        if (!data) return;
        const previousIds = getValues().userIds;
        const newIds = enterpriseSet.size
            ? previousIds.filter((id) => id === currentUser.id || users.some((user) => user.id === id))
            : [currentUser.id];
        setValue('userIds', newIds);
    }, [data, getValues, setValue, users, currentUser, enterpriseSet]);

    return (
        <SelectField
            {...selectProps}
            compact
            loader={isLoading}
            multiselect
            options={renderOptions(users, currentUser.id, disableCurrentUser)}
            virtualized={users.length > VIRTUALIZED_OPTIONS_LIMIT}
        />
    );
};
